/**
 * Feedback Dashboard Component
 * Administrative interface for viewing and managing user feedback
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { 
  FeedbackWithStatus, 
  FeedbackFilter, 
  FeedbackSort, 
  FeedbackType,
  FeedbackAnalytics
} from '../../types/feedback';
import { useFeedbackService } from '../../hooks/useFeedbackService';
import FeedbackAnalyticsCard from './dashboard/FeedbackAnalyticsCard';
import FeedbackFilters from './dashboard/FeedbackFilters';
import FeedbackList from './dashboard/FeedbackList';
import FeedbackDetail from './dashboard/FeedbackDetail';

const FeedbackDashboard: React.FC = () => {
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackWithStatus | null>(null);
  const [filters, setFilters] = useState<FeedbackFilter>({});
  const [sort, setSort] = useState<FeedbackSort>({
    field: 'timestamp',
    direction: 'desc'
  });
  const [viewMode, setViewMode] = useState<'list' | 'analytics'>('list');
  
  const {
    getFeedback,
    getAnalytics,
    exportFeedback,
    clearLocalQueue,
    syncPendingFeedback,
    pendingCount,
    deleteFeedback,
    updateFeedbackStatus,
    archiveFeedback
  } = useFeedbackService();

  const [feedback, setFeedback] = useState<FeedbackWithStatus[]>([]);
  const [analytics, setAnalytics] = useState<FeedbackAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [showNewNotification, setShowNewNotification] = useState(false);
  const [newFeedbackIds, setNewFeedbackIds] = useState<Set<string>>(new Set());

  // Load data on mount and when filters change
  useEffect(() => {
    loadData();
  }, [filters, sort]);

  // Event-driven real-time updates
  useEffect(() => {
    const handleFeedbackSubmitted = (event: CustomEvent) => {
      console.log('Dashboard detected new feedback:', event.detail);
      setShowNewNotification(true);
      setTimeout(() => setShowNewNotification(false), 3000);
      loadData(); // Refresh data when new feedback is submitted
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData(); // Refresh when user returns to page
      }
    };

    window.addEventListener('feedbackSubmitted', handleFeedbackSubmitted as EventListener);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('feedbackSubmitted', handleFeedbackSubmitted as EventListener);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Clear new badges after 30 seconds
  useEffect(() => {
    if (newFeedbackIds.size > 0) {
      const timer = setTimeout(() => {
        setNewFeedbackIds(new Set());
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [newFeedbackIds]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const feedbackData = await getFeedback(filters, sort);
      setFeedback(feedbackData || []);
      
      if (viewMode === 'analytics') {
        const analyticsData = await getAnalytics();
        setAnalytics(analyticsData);
      }
    } catch (error) {
      console.error('Failed to load feedback data:', error);
      setFeedback([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters: FeedbackFilter) => {
    setFilters(newFilters);
  };

  const handleSortChange = (field: FeedbackSort['field']) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleExport = (format: 'csv' | 'json' | 'xlsx') => {
    exportFeedback(format, filters);
  };

  const handleSyncPending = async () => {
    try {
      await syncPendingFeedback();
      loadData(); // Reload after sync
    } catch (error) {
      console.error('Sync failed:', error);
    }
  };

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    await loadData();
    setIsManualRefreshing(false);
  };

  const handleFeedbackSelect = (feedback: FeedbackWithStatus) => {
    setSelectedFeedback(feedback);
  };

  const handleFeedbackClose = () => {
    setSelectedFeedback(null);
  };

  const handleDeleteFeedback = async (feedbackId: string) => {
    await deleteFeedback(feedbackId);
    loadData(); // Refresh data after deletion
  };

  const handleArchiveFeedback = async (feedbackId: string) => {
    await archiveFeedback(feedbackId);
    loadData(); // Refresh data after archiving
  };

  const handleUpdateFeedbackStatus = async (feedbackId: string, status: string) => {
    await updateFeedbackStatus(feedbackId, status);
    loadData(); // Refresh data after status update
  };

  const getFeedbackTypeIcon = (type: FeedbackType) => {
    const icons = {
      'quick-reaction': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
        </svg>
      ),
      'page-comment': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      'bug-report': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="8" y="6" width="8" height="14" rx="4"/>
          <path d="M6 6h4"/>
          <path d="M6 10h4"/>
          <path d="M6 14h4"/>
          <path d="M14 6h4"/>
          <path d="M14 10h4"/>
          <path d="M14 14h4"/>
        </svg>
      ),
      'feature-request': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={3}/>
          <path d="M12 1v6m0 6v6"/>
          <path d="M21 12h-6m-6 0H3"/>
        </svg>
      ),
      'element-feedback': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 9l-4 4v3a2 2 0 0 0 2 2h3l4-4"/>
          <path d="M14 5l4 4-6.5 6.5a2 2 0 0 1-3-3L14 5z"/>
        </svg>
      ),
      'flow-rating': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ),
      'general': (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      )
    };
    return icons[type];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-6 border-b-2 border-ink-200">
        <div>
          <h1 className="text-3xl font-display text-ink-900 mb-2">
            Feedback Dashboard
          </h1>
          <p className="text-ink-600">
            Monitor and manage user feedback for the CLEAR platform
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <button
              onClick={handleSyncPending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-50 text-amber-700 
                         border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              Sync {pendingCount} Pending
            </button>
          )}

          {/* Manual Refresh Button */}
          <button
            onClick={handleManualRefresh}
            disabled={isManualRefreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-slate-700 
                       border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors
                       disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed"
            title="Refresh feedback data"
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              className={isManualRefreshing ? 'animate-spin' : ''}
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            {isManualRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          
          <div className="flex rounded-lg border border-stone-200 overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors',
                viewMode === 'list'
                  ? 'bg-accent-500 text-white'
                  : 'bg-white text-slate-700 hover:bg-stone-50'
              )}
            >
              List View
            </button>
            <button
              onClick={() => setViewMode('analytics')}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors',
                viewMode === 'analytics'
                  ? 'bg-accent-500 text-white'
                  : 'bg-white text-slate-700 hover:bg-stone-50'
              )}
            >
              Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Feedback</p>
              <p className="text-2xl font-bold text-slate-900">{feedback.length}</p>
            </div>
            <div className="w-12 h-12 bg-accent-500-50 rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-600">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">This Week</p>
              <p className="text-2xl font-bold text-slate-900">
                {feedback.filter(f => {
                  const date = new Date(f.timestamp);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return date >= weekAgo;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-sage-50 rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sage-600">
                <path d="M3 3v18h18"/>
                <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Bug Reports</p>
              <p className="text-2xl font-bold text-slate-900">
                {feedback.filter(f => f.type === 'bug-report').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-coral-50 rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-coral-600">
                <rect x="8" y="6" width="8" height="14" rx="4"/>
                <path d="M6 6h4"/>
                <path d="M6 10h4"/>
                <path d="M6 14h4"/>
                <path d="M14 6h4"/>
                <path d="M14 10h4"/>
                <path d="M14 14h4"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Feature Requests</p>
              <p className="text-2xl font-bold text-slate-900">
                {feedback.filter(f => f.type === 'feature-request').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-steel-50 rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-steel-600">
                <circle cx="12" cy="12" r={3}/>
                <path d="M12 1v6m0 6v6"/>
                <path d="M21 12h-6m-6 0H3"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg border border-stone-200 sticky top-6">
            <FeedbackFilters
              filters={filters}
              onChange={handleFilterChange}
              feedbackTypes={['page-comment', 'bug-report', 'feature-request', 'quick-reaction', 'general']}
            />
            
            {/* Export Actions */}
            <div className="mt-8 pt-6 border-t border-stone-200">
              <h3 className="text-sm font-medium text-slate-800 mb-3">Export Data</h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleExport('json')}
                  className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-stone-50 rounded-lg transition-colors"
                >
                  Export as JSON
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-stone-50 rounded-lg transition-colors"
                >
                  Export as CSV
                </button>
              </div>
            </div>

            {/* Data Management */}
            <div className="mt-6 pt-6 border-t border-stone-200">
              <h3 className="text-sm font-medium text-slate-800 mb-3">Data Management</h3>
              <button
                onClick={clearLocalQueue}
                className="w-full text-left px-3 py-2 text-sm text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
              >
                Clear Local Queue
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          {viewMode === 'analytics' ? (
            <FeedbackAnalyticsCard 
              analytics={analytics || getAnalytics()} 
              isLoading={isLoading}
            />
          ) : (
            <div className="bg-white rounded-lg border border-stone-200">
              <div className="p-6 border-b border-stone-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-slate-800">
                    Feedback Items ({feedback.length})
                  </h2>
                  
                  {/* Sort Controls */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Sort by:</span>
                    <select
                      value={sort.field}
                      onChange={(e) => handleSortChange(e.target.value as FeedbackSort['field'])}
                      className="text-sm border border-stone-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta"
                    >
                      <option value="timestamp">Date</option>
                      <option value="priority">Priority</option>
                      <option value="status">Status</option>
                    </select>
                    <button
                      onClick={() => handleSortChange(sort.field)}
                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        className={cn(
                          'transform transition-transform',
                          sort.direction === 'desc' && 'rotate-180'
                        )}
                      >
                        <path d="M12 5v14"/>
                        <path d="M5 12l7-7 7 7"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <FeedbackList
                feedback={feedback}
                onSelect={handleFeedbackSelect}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </div>

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <FeedbackDetail
          feedback={selectedFeedback}
          onClose={handleFeedbackClose}
          onDelete={handleDeleteFeedback}
          onArchive={handleArchiveFeedback}
          onStatusChange={handleUpdateFeedbackStatus}
        />
      )}

      {/* New Feedback Notification */}
      {showNewNotification && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg animate-slide-in">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
            <span>New feedback received!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDashboard;